import { useState, useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

export default function AboutPage() {
  const [counts, setCounts] = useState({
    patients: 0,
    success: 0,
    years: 0,
    sessions: 0,
  });

  useEffect(() => {
    const targets = {
      patients: 500,
      success: 95,
      years: 5,
      sessions: 1000,
    };

    const duration = 2000;
    const steps = 50;
    const interval = duration / steps;

    const timer = setInterval(() => {
      setCounts((prev) => {
        const newCounts = { ...prev };
        let allComplete = true;

        Object.keys(targets).forEach((key) => {
          const target = targets[key as keyof typeof targets];
          const current = prev[key as keyof typeof prev];
          if (current < target) {
            allComplete = false;
            newCounts[key as keyof typeof newCounts] = Math.min(
              current + Math.ceil(target / steps),
              target
            );
          }
        });

        if (allComplete) {
          clearInterval(timer);
        }

        return newCounts;
      });
    }, interval);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-primary to-secondary text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 right-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 left-10 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center animate-fade-in">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">من نحن</h1>
              <p className="text-xl md:text-2xl text-white/90 leading-relaxed">
                جمعية خيرية متخصصة في <strong>مكافحة التدخين بالأفلاج</strong>
              </p>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className="animate-slide-up">
                  <img
                    src="https://public.readdy.ai/ai/img_res/cbdd990d84f52bf5a483699e243ef6e1.jpg"
                    alt="مبنى عيادة الإقلاع"
                    className="rounded-2xl shadow-2xl w-full object-cover"
                  />
                </div>
                <div className="animate-slide-up" style={{ animationDelay: '0.2s' }}>
                  <h2 className="text-4xl font-bold text-primary mb-6">قصتنا</h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed text-lg">
                    <p>
                      <strong>جمعية خيرية متخصصة في مكافحة التدخين بالأفلاج</strong>، تأسست بهدف مساعدة المدخنين على الإقلاع عن التدخين من خلال تقديم خدمات طبية وتوعوية مجانية ومتخصصة.
                    </p>
                    <p>
                      نؤمن بأن <strong>الإقلاع عن التدخين</strong> ليس مجرد قرار فردي، بل رحلة تحتاج إلى دعم طبي ونفسي متخصص. لذلك نوفر فريقاً طبياً مؤهلاً وبرامج علاجية شاملة تساعد المدخنين على تحقيق هدفهم.
                    </p>
                    <p>
                      منذ تأسيسنا، ساعدنا <strong>المئات من المرضى</strong> على الإقلاع عن التدخين وتحسين صحتهم ونوعية حياتهم. نفخر بنسبة نجاح عالية ومتابعة مستمرة لكل مريض.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Achievements Section */}
        <section className="py-20 bg-gradient-to-br from-primary/5 to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">إنجازاتنا</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                أرقام تعكس <strong>التزامنا بخدمة المجتمع</strong> ومساعدة المدخنين
              </p>
              <div className="w-24 h-1 bg-accent mx-auto mt-4"></div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
              <div className="bg-white rounded-2xl p-8 shadow-lg text-center animate-slide-up hover:shadow-2xl transition-shadow duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-user-heart-line text-white text-3xl"></i>
                </div>
                <div className="text-5xl font-bold text-primary mb-2">{counts.patients}+</div>
                <div className="text-gray-600 font-medium">مريض</div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg text-center animate-slide-up hover:shadow-2xl transition-shadow duration-300" style={{ animationDelay: '0.1s' }}>
                <div className="w-16 h-16 bg-gradient-to-br from-secondary to-secondary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-trophy-line text-white text-3xl"></i>
                </div>
                <div className="text-5xl font-bold text-secondary mb-2">{counts.success}%</div>
                <div className="text-gray-600 font-medium">نسبة النجاح</div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg text-center animate-slide-up hover:shadow-2xl transition-shadow duration-300" style={{ animationDelay: '0.2s' }}>
                <div className="w-16 h-16 bg-gradient-to-br from-accent to-accent-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-line text-white text-3xl"></i>
                </div>
                <div className="text-5xl font-bold text-accent mb-2">{counts.years}+</div>
                <div className="text-gray-600 font-medium">سنوات خبرة</div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg text-center animate-slide-up hover:shadow-2xl transition-shadow duration-300" style={{ animationDelay: '0.3s' }}>
                <div className="w-16 h-16 bg-gradient-to-br from-primary-600 to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-calendar-check-line text-white text-3xl"></i>
                </div>
                <div className="text-5xl font-bold text-primary-600 mb-2">{counts.sessions}+</div>
                <div className="text-gray-600 font-medium">جلسة علاجية</div>
              </div>
            </div>
          </div>
        </section>

        {/* Medical Services Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">خدماتنا الطبية</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                نقدم <strong>برامج علاجية متكاملة</strong> تشمل جميع جوانب الإقلاع عن التدخين
              </p>
              <div className="w-24 h-1 bg-accent mx-auto mt-4"></div>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[
                {
                  icon: 'ri-stethoscope-line',
                  title: 'الفحص الطبي الشامل',
                  description: 'تقييم شامل للحالة الصحية وتحديد مستوى الإدمان على النيكوتين',
                  color: 'from-primary to-primary-600'
                },
                {
                  icon: 'ri-medicine-bottle-line',
                  title: 'العلاج الدوائي',
                  description: 'وصف الأدوية المساعدة على الإقلاع مع متابعة الآثار الجانبية',
                  color: 'from-secondary to-secondary-600'
                },
                {
                  icon: 'ri-mental-health-line',
                  title: 'العلاج النفسي',
                  description: 'جلسات علاج نفسي للتعامل مع التحديات النفسية والقلق',
                  color: 'from-accent to-accent-600'
                },
                {
                  icon: 'ri-user-voice-line',
                  title: 'العلاج السلوكي',
                  description: 'تغيير العادات والسلوكيات المرتبطة بالتدخين',
                  color: 'from-primary-600 to-secondary'
                },
                {
                  icon: 'ri-team-line',
                  title: 'الدعم الجماعي',
                  description: 'جلسات دعم جماعية مع مرضى آخرين في نفس الرحلة',
                  color: 'from-secondary-600 to-accent'
                },
                {
                  icon: 'ri-calendar-check-line',
                  title: 'المتابعة الدورية',
                  description: 'متابعة مستمرة لضمان عدم الانتكاسة والحفاظ على النجاح',
                  color: 'from-accent-600 to-primary'
                }
              ].map((service, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center mb-6 shadow-lg`}>
                    <i className={`${service.icon} text-white text-3xl`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-20 bg-gradient-to-br from-primary/5 to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">فريقنا الطبي</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                <strong>أطباء متخصصون ومعتمدون</strong> في علاج الإدمان والطب النفسي
              </p>
              <div className="w-24 h-1 bg-accent mx-auto mt-4"></div>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[
                {
                  name: 'د. محمد العتيبي',
                  title: 'استشاري علاج الإدمان',
                  image: 'https://readdy.ai/api/search-image?query=Professional%20Saudi%20Arabian%20male%20doctor%20in%20white%20coat%2C%20confident%20medical%20professional%2C%20healthcare%20specialist%2C%20clinical%20setting%2C%20medical%20expertise%2C%20compassionate%20physician%2C%20professional%20portrait&width=400&height=500&seq=doctor-001&orientation=portrait'
                },
                {
                  name: 'د. فاطمة الشمري',
                  title: 'استشارية الطب النفسي',
                  image: 'https://readdy.ai/api/search-image?query=Professional%20Saudi%20Arabian%20female%20doctor%20wearing%20hijab%20and%20white%20coat%2C%20confident%20medical%20professional%2C%20healthcare%20specialist%2C%20clinical%20setting%2C%20medical%20expertise%2C%20compassionate%20physician%2C%20professional%20portrait&width=400&height=500&seq=doctor-002&orientation=portrait'
                },
                {
                  name: 'د. خالد المطيري',
                  title: 'أخصائي العلاج السلوكي',
                  image: 'https://readdy.ai/api/search-image?query=Professional%20Saudi%20Arabian%20male%20doctor%20in%20white%20coat%2C%20confident%20medical%20professional%2C%20healthcare%20specialist%2C%20clinical%20setting%2C%20medical%20expertise%2C%20compassionate%20physician%2C%20professional%20portrait&width=400&height=500&seq=doctor-003&orientation=portrait'
                }
              ].map((doctor, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src={doctor.image}
                      alt={doctor.name}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  </div>
                  <div className="p-6 text-center">
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">{doctor.name}</h3>
                    <p className="text-primary font-medium">{doctor.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}